import React, { useState, useEffect } from 'react';
import { 
  Box, Paper, Typography, Button, CircularProgress, Alert, 
  Grid, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, LinearProgress, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import Plot from 'react-plotly.js';
import axios from 'axios';

const MOM = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [selectedSensor, setSelectedSensor] = useState('');
  const [selectedModelType, setSelectedModelType] = useState('autoencoder');
  const [dataFiles, setDataFiles] = useState([]);
  const [selectedSource, setSelectedSource] = useState('');

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const res = await axios.get('/api/analysis-files');
        const files = res.data?.files || [];
        setDataFiles(files);
        if (files.length > 0) {
          setSelectedSource(res.data?.default || files[0].id);
        }
      } catch {
        setDataFiles([]);
      }
    };
    fetchFiles();
  }, []);

  const runMOMAnalysis = async () => {
    setLoading(true);
    setError(null);
    // Don't clear previous results - keep them visible
    // setResults(null);
    
    if (!selectedSource) {
      setError('Select a data file first.');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get('/api/mom', {
        params: {
          model_type: selectedModelType,
          source: selectedSource
        },
        timeout: 1200000
      });
      
      if (response.data.error) {
        setError(response.data.error);
      } else {
        setResults(response.data);
      }
    } catch (err) {
      if (err.code === 'ECONNABORTED') {
        setError('Request timeout - MOM analysis is taking too long. Please try again.');
      } else if (err.response) {
        setError(err.response.data?.error || `Server error: ${err.response.status}`);
      } else if (err.request) {
        setError('Network error - Cannot connect to server. Please check if the backend is running.');
      } else {
        setError(err.message || 'Failed to run MOM analysis');
      }
    } finally {
      setLoading(false);
    }
  };

  const getPlotDataForSensor = (sensorName) => {
    if (!results || !results.anomaly_scores) return [];
    
    // Filter scores for this specific sensor
    const sensorScores = results.anomaly_scores.filter(item => item.strain_type === sensorName);
    if (sensorScores.length === 0) return [];
    
    const scores = sensorScores.map(item => item.score);
    const timestamps = sensorScores.map(item => {
      const date = new Date(item.timestamp * 1000);
      return date.toISOString();
    });
    const isAnomaly = sensorScores.map(item => item.is_anomaly);
    
    const sensorInfo = results.individual_model_info?.[sensorName];
    const baseThreshold = Number.isFinite(sensorInfo?.base_threshold)
      ? Number(sensorInfo.base_threshold)
      : null;
    const criticalThreshold = Number.isFinite(sensorInfo?.critical_threshold)
      ? Number(sensorInfo.critical_threshold)
      : (Number.isFinite(sensorInfo?.threshold) ? Number(sensorInfo.threshold) : 0);
    const thresholdX = timestamps.length > 0 ? [timestamps[0], timestamps[timestamps.length - 1]] : [];
    const traces = [
      {
        x: timestamps,
        y: scores,
        type: 'scatter',
        mode: 'lines+markers',
        name: 'Reconstruction Error',
        line: { color: '#1f77b4', width: 2 },
        marker: {
          size: 5,
          color: scores.map((score, idx) => isAnomaly[idx] ? '#ff4444' : '#1f77b4'),
          line: { width: 1, color: scores.map((score, idx) => isAnomaly[idx] ? '#cc0000' : '#0d5aa7') }
        }
      }
    ];
    if (baseThreshold != null && baseThreshold > 0) {
      traces.push({
        x: thresholdX,
        y: [baseThreshold, baseThreshold],
        type: 'scatter',
        mode: 'lines',
        name: `Base Threshold (${baseThreshold.toFixed(6)})`,
        line: { color: '#2ca02c', width: 2, dash: 'dot' },
        hoverinfo: 'y+name',
        showlegend: true
      });
    }
    if (criticalThreshold > 0) {
      traces.push({
        x: thresholdX,
        y: [criticalThreshold, criticalThreshold],
        type: 'scatter',
        mode: 'lines',
        name: `Critical Threshold (${criticalThreshold.toFixed(6)})`,
        line: { color: '#ff0000', width: 3, dash: 'dash' },
        hoverinfo: 'y+name',
        showlegend: true
      });
    }
    return traces;
  };

  return (
    <Box sx={{ p: 3, pt: '80px', maxWidth: 1400, mx: 'auto' }}>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          MOM (Model of Models) Analysis
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Choose B27 (training/, MUX format), M16 (endpier1/, MUX format), or dataaaa1 (train 2/, Strain/TT). Train per strain on 80% of the selected file,
          then test on the remaining 20%.
          The model learns each strain sensor's temporal behavior and evaluates reconstruction error on the test split.
        </Typography>
        
              {results && results.baseline_calibration && results.baseline_calibration.applied && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Baseline Calibration Applied
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Autoencoder: same training setup as Data Analysis (strain+TT, 80/20 split, MinMax, 150 epochs).
                    Red markers = score above base threshold; critical line shown for reference only.
                    LSTM: same data split (strain+TT); anomalies use LSTM model threshold.
                    Each dataset is calibrated independently to preserve relative patterns within each dataset.
                  </Typography>
                  {results.baseline_calibration.training_baselines && Object.keys(results.baseline_calibration.training_baselines).length > 0 && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                      <strong>Training Baselines:</strong> {Object.entries(results.baseline_calibration.training_baselines)
                        .slice(0, 3)
                        .map(([sensor, baseline]) => `${sensor}: ${baseline.toFixed(2)}`)
                        .join(', ')}
                      {Object.keys(results.baseline_calibration.training_baselines).length > 3 && ' ...'}
                    </Typography>
                  )}
                  {results.baseline_calibration.testing_baselines && Object.keys(results.baseline_calibration.testing_baselines).length > 0 && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                      <strong>Testing Baselines:</strong> {Object.entries(results.baseline_calibration.testing_baselines)
                        .slice(0, 3)
                        .map(([sensor, baseline]) => `${sensor}: ${baseline.toFixed(2)}`)
                        .join(', ')}
                      {Object.keys(results.baseline_calibration.testing_baselines).length > 3 && ' ...'}
                    </Typography>
                  )}
                </Alert>
              )}

        <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 320 }} disabled={loading || dataFiles.length === 0}>
            <InputLabel id="data-file-label">Data File</InputLabel>
            <Select
              labelId="data-file-label"
              value={selectedSource}
              label="Data File"
              onChange={(e) => setSelectedSource(e.target.value)}
            >
              {dataFiles.map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  {f.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel id="select-model-label">Select Model Type</InputLabel>
            <Select
              labelId="select-model-label"
              id="select-model"
              value={selectedModelType}
              label="Select Model Type"
              onChange={(e) => setSelectedModelType(e.target.value)}
              disabled={loading}
            >
              <MenuItem value="autoencoder">
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Autoencoder</Typography>
                  <Typography variant="caption" color="text.secondary">Feedforward neural network (default, fast)</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="lstm">
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>LSTM Autoencoder</Typography>
                  <Typography variant="caption" color="text.secondary">Better temporal pattern learning (slower)</Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
          
          <Button
            variant="contained"
            color="primary"
            onClick={runMOMAnalysis}
            disabled={loading || !selectedSource}
            size="large"
            sx={{ minWidth: 200 }}
          >
            {loading ? <CircularProgress size={24} sx={{ mr: 2 }} /> : null}
            {loading ? 'Running Analysis...' : 'Run MOM Analysis'}
          </Button>
        </Box>
        
        {results && results.model_info && results.model_info.model_type && (
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Model Used:</strong> {results.model_info.model_type.charAt(0).toUpperCase() + results.model_info.model_type.slice(1).replace('_', ' ')}
            </Typography>
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ mt: 3 }}>
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
              Training {selectedModelType === 'lstm' ? 'LSTM autoencoder' : selectedModelType === 'isolation_forest' ? 'Isolation Forest' : 'autoencoder'} and analyzing data...
            </Typography>
          </Box>
        )}

        {results && (
          <Box sx={{ mt: 4 }}>
            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Training Data Points
                    </Typography>
                    <Typography variant="h4">
                      {results.training_period.data_points}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {results.training_period.start && results.training_period.end 
                        ? `${results.training_period.start.split(' ')[0]} to ${results.training_period.end.split(' ')[0]}`
                        : 'All available data'}
                      {results.training_period.sensors_trained && ` (${results.training_period.sensors_trained} individual models)`}
                      {results.training_period.training_files && ` - Files: ${results.training_period.training_files.join(', ')}`}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Test Data Points
                    </Typography>
                    <Typography variant="h4">
                      {results.results.total_test_points}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {results.testing_period.start && results.testing_period.end 
                        ? `${results.testing_period.start.split(' ')[0]} to ${results.testing_period.end.split(' ')[0]}`
                        : 'All available data'}
                      {results.testing_period.testing_file && ` - File: ${results.testing_period.testing_file}`}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Anomalies Detected
                    </Typography>
                    <Typography variant="h4" color="error">
                      {results.results.anomaly_count}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {results.results.anomaly_percentage}% of test data
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Models Trained
                    </Typography>
                    <Typography variant="h4">
                      {results.model_info.models_trained || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Individual autoencoder per sensor
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Individual Strain Graphs with Dropdown */}
            {results.sensor_results && Object.keys(results.sensor_results).length > 0 && (
              <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Individual Strain Reconstruction Error Graph
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select a strain sensor to view its reconstruction error over time. Red markers indicate anomalies (score above base threshold).
                </Typography>
                
                {/* Sensor Dropdown */}
                <Box sx={{ mb: 3, minWidth: 250 }}>
                  <FormControl fullWidth>
                    <InputLabel id="sensor-select-label">Select Strain Sensor</InputLabel>
                    <Select
                      labelId="sensor-select-label"
                      id="sensor-select"
                      value={selectedSensor || ''}
                      label="Select Strain Sensor"
                      onChange={(e) => setSelectedSensor(e.target.value)}
                      sx={{ bgcolor: 'white' }}
                    >
                      {Object.keys(results.sensor_results)
                        .sort((a, b) => {
                          // Sort by strain number
                          const numA = parseInt(a.match(/\d+/)?.[0] || '0');
                          const numB = parseInt(b.match(/\d+/)?.[0] || '0');
                          return numA - numB;
                        })
                        .map((sensor) => (
                          <MenuItem key={sensor} value={sensor}>
                            {sensor}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Display Graph for Selected Sensor */}
                {selectedSensor && results.sensor_results[selectedSensor] && (
                  <Box>
                    {(() => {
                      const plotData = getPlotDataForSensor(selectedSensor);
                      const sensorInfo = results.individual_model_info?.[selectedSensor];
                      const data = results.sensor_results[selectedSensor];
                      const baseThreshold = Number.isFinite(sensorInfo?.base_threshold)
                        ? Number(sensorInfo.base_threshold)
                        : null;
                      const criticalThreshold = Number.isFinite(sensorInfo?.critical_threshold)
                        ? Number(sensorInfo.critical_threshold)
                        : (Number.isFinite(sensorInfo?.threshold) ? Number(sensorInfo.threshold) : 0);
                      
                      // Get timestamps for annotation
                      const sensorScores = results.anomaly_scores.filter(item => item.strain_type === selectedSensor);
                      const timestamps = sensorScores.map(item => {
                        const date = new Date(item.timestamp * 1000);
                        return date.toISOString();
                      });
                      
                      if (plotData.length === 0) {
                        return (
                          <Alert severity="info">
                            No data available for {selectedSensor}
                          </Alert>
                        );
                      }
                      
                      // Get temperature data for this sensor
                      const temperatures = sensorScores.map(item => item.temperature).filter(t => t !== null && t !== undefined);
                      const tempTimestamps = sensorScores
                        .filter(item => item.temperature !== null && item.temperature !== undefined)
                        .map(item => {
                          const date = new Date(item.timestamp * 1000);
                          return date.toISOString();
                        });
                      
                      // Temperature plot data
                      const tempPlotData = tempTimestamps.length > 0 ? [
                        {
                          x: tempTimestamps,
                          y: temperatures,
                          type: 'scatter',
                          mode: 'lines+markers',
                          name: 'Temperature (°C)',
                          line: { color: '#ff9800', width: 2 },
                          marker: { size: 4, color: '#ff9800' }
                        }
                      ] : [];
                      
                      // Get strain data for this sensor
                      const strainValues = sensorScores.map(item => item.strain_value).filter(s => s !== null && s !== undefined);
                      const strainTimestamps = sensorScores
                        .filter(item => item.strain_value !== null && item.strain_value !== undefined)
                        .map(item => {
                          const date = new Date(item.timestamp * 1000);
                          return date.toISOString();
                        });
                      
                      // Strain plot data
                      const strainPlotData = strainTimestamps.length > 0 ? [
                        {
                          x: strainTimestamps,
                          y: strainValues,
                          type: 'scatter',
                          mode: 'lines+markers',
                          name: 'Strain Value',
                          line: { color: '#2196f3', width: 2 },
                          marker: { size: 4, color: '#2196f3' }
                        }
                      ] : [];
                      
                      return (
                        <Box>
                          {/* Reconstruction Error Graph */}
                          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                              {selectedSensor} - Reconstruction Error
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                              {baseThreshold != null && (
                                <>Base: <strong style={{ color: '#2ca02c' }}>{baseThreshold.toFixed(6)}</strong>{' | '}</>
                              )}
                              Critical (reference): <strong style={{ color: '#ff0000' }}>{criticalThreshold.toFixed(6)}</strong>
                              {' | '}Test Points: {data.total_points}
                              {' | '}Anomalies (vs base): {data.anomaly_count} ({data.anomaly_percentage.toFixed(2)}%)
                            </Typography>
                            <Plot
                              data={plotData}
                              layout={{
                                title: `${selectedSensor} - Reconstruction Error Over Time`,
                                xaxis: { title: 'Date' },
                                yaxis: { 
                                  title: 'Reconstruction Error (MSE)',
                                  zeroline: false,
                                  showgrid: true
                                },
                                height: 500,
                                hovermode: 'closest',
                                margin: { t: 60, b: 60, l: 70, r: 40 },
                                shapes: [
                                  ...(baseThreshold != null && baseThreshold > 0 ? [{
                                    type: 'line',
                                    xref: 'paper',
                                    x0: 0,
                                    x1: 1,
                                    yref: 'y',
                                    y0: baseThreshold,
                                    y1: baseThreshold,
                                    line: { color: '#2ca02c', width: 2, dash: 'dot' },
                                    layer: 'above'
                                  }] : []),
                                  ...(criticalThreshold > 0 ? [{
                                    type: 'line',
                                    xref: 'paper',
                                    x0: 0,
                                    x1: 1,
                                    yref: 'y',
                                    y0: criticalThreshold,
                                    y1: criticalThreshold,
                                    line: { color: '#ff0000', width: 3, dash: 'dash' },
                                    layer: 'above'
                                  }] : [])
                                ],
                                annotations: [
                                  ...(timestamps.length > 0 && baseThreshold != null && baseThreshold > 0 ? [{
                                    x: timestamps[0],
                                    y: baseThreshold,
                                    text: `Base: ${baseThreshold.toFixed(6)}`,
                                    showarrow: false,
                                    bgcolor: 'rgba(255, 255, 255, 0.95)',
                                    bordercolor: '#2ca02c',
                                    borderwidth: 1,
                                    font: { color: '#2ca02c', size: 11 }
                                  }] : []),
                                  ...(timestamps.length > 0 && criticalThreshold > 0 ? [{
                                    x: timestamps[Math.floor(timestamps.length / 2)],
                                    y: criticalThreshold,
                                    text: `Critical: ${criticalThreshold.toFixed(6)}`,
                                    showarrow: true,
                                    arrowhead: 2,
                                    arrowcolor: '#ff0000',
                                    bgcolor: 'rgba(255, 255, 255, 0.95)',
                                    bordercolor: '#ff0000',
                                    borderwidth: 2,
                                    font: { color: '#ff0000', size: 12, family: 'Arial Black' },
                                    xanchor: 'center',
                                    yanchor: 'bottom'
                                  }] : [])
                                ]
                              }}
                              style={{ width: '100%' }}
                            />
                          </Paper>
                          
                          {/* Temperature Graph */}
                          {tempPlotData.length > 0 && (
                            <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
                              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                {selectedSensor} - Temperature Over Time
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                                Temperature readings corresponding to {selectedSensor}
                              </Typography>
                              <Plot
                                data={tempPlotData}
                                layout={{
                                  title: `${selectedSensor} - Temperature Over Time`,
                                  xaxis: { title: 'Date' },
                                  yaxis: { 
                                    title: 'Temperature (°C)',
                                    zeroline: false,
                                    showgrid: true
                                  },
                                  height: 400,
                                  hovermode: 'closest',
                                  margin: { t: 60, b: 60, l: 70, r: 40 }
                                }}
                                style={{ width: '100%' }}
                              />
                            </Paper>
                          )}
                          
                          {/* Strain Graph */}
                          {strainPlotData.length > 0 && (
                            <Paper elevation={2} sx={{ p: 2 }}>
                              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                {selectedSensor} - Original Strain Over Time
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                                Original (uncalibrated) strain values for {selectedSensor}
                              </Typography>
                                <Plot
                                  data={strainPlotData}
                                  layout={{
                                    title: `${selectedSensor} - Original Strain Over Time`,
                                    xaxis: { title: 'Date' },
                                    yaxis: { 
                                      title: 'Original Strain Value (Uncalibrated)',
                                      zeroline: false,
                                      showgrid: true
                                    },
                                    height: 400,
                                    hovermode: 'closest',
                                    margin: { t: 60, b: 60, l: 70, r: 40 }
                                  }}
                                  style={{ width: '100%' }}
                                />
                            </Paper>
                          )}
                        </Box>
                      );
                    })()}
                  </Box>
                )}

                {!selectedSensor && (
                  <Alert severity="info">
                    Please select a strain sensor from the dropdown above to view its graph.
                  </Alert>
                )}
              </Paper>
            )}

            {/* Individual Strain Results */}
            {results.sensor_results && Object.keys(results.sensor_results).length > 0 && (
              <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Individual Strain Results (Each strain has its own trained model)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Each strain was trained and tested individually with its own autoencoder model
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Strain Sensor</strong></TableCell>
                        <TableCell align="right">Test Points</TableCell>
                        <TableCell align="right">Anomalies</TableCell>
                        <TableCell align="right">Anomaly %</TableCell>
                        <TableCell align="right">Base Threshold</TableCell>
                        <TableCell align="right">Critical Threshold</TableCell>
                        <TableCell>Anomaly Dates (Top 5)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(results.sensor_results)
                        .sort(([a], [b]) => {
                          // Sort by strain number
                          const numA = parseInt(a.match(/\d+/)?.[0] || '0');
                          const numB = parseInt(b.match(/\d+/)?.[0] || '0');
                          return numA - numB;
                        })
                        .map(([sensor, data]) => {
                          const sensorInfo = results.model_info?.per_sensor_info?.[sensor];
                          return (
                            <TableRow key={sensor}>
                              <TableCell><strong>{sensor}</strong></TableCell>
                              <TableCell align="right">{data.total_points || 0}</TableCell>
                              <TableCell align="right">
                                <Chip 
                                  label={data.anomaly_count} 
                                  color={data.anomaly_count > 0 ? 'error' : 'success'} 
                                  size="small" 
                                />
                              </TableCell>
                              <TableCell align="right">{data.anomaly_percentage.toFixed(2)}%</TableCell>
                              <TableCell align="right">
                                {Number.isFinite(sensorInfo?.base_threshold)
                                  ? Number(sensorInfo.base_threshold).toFixed(6)
                                  : 'N/A'}
                              </TableCell>
                              <TableCell align="right">
                                {Number.isFinite(sensorInfo?.critical_threshold)
                                  ? Number(sensorInfo.critical_threshold).toFixed(6)
                                  : (sensorInfo?.threshold ? Number(sensorInfo.threshold).toFixed(6) : 'N/A')}
                              </TableCell>
                              <TableCell>
                                {data.anomaly_dates && data.anomaly_dates.length > 0 ? (
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {data.anomaly_dates.slice(0, 5).map((date, idx) => (
                                      <Chip key={idx} label={date} size="small" color="error" />
                                    ))}
                                    {data.anomaly_dates.length > 5 && (
                                      <Chip label={`+${data.anomaly_dates.length - 5} more`} size="small" />
                                    )}
                                  </Box>
                                ) : (
                                  <Chip label="None" size="small" color="success" />
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {/* Anomaly Dates Table */}
            {results.results.anomaly_dates.length > 0 && (
              <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Overall Anomaly Dates ({results.results.total_anomaly_days} unique days)
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {results.results.anomaly_dates.map((date, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{date}</TableCell>
                          <TableCell>
                            <Chip label="Anomaly" color="error" size="small" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {/* Exact Anomaly Points */}
            {results.anomaly_scores && results.anomaly_scores.some((item) => item.is_anomaly) && (
              <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Exact Anomaly Points (Timestamp, Strain, Reconstruction Error)
                </Typography>
                <TableContainer sx={{ maxHeight: 420 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Timestamp</strong></TableCell>
                        <TableCell><strong>Sensor</strong></TableCell>
                        <TableCell align="right"><strong>Strain Value</strong></TableCell>
                        <TableCell align="right"><strong>Reconstruction Error (MSE)</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {results.anomaly_scores
                        .filter((item) => item.is_anomaly)
                        .map((item, idx) => (
                          <TableRow key={`anom-${idx}`}>
                            <TableCell>{item.datetime}</TableCell>
                            <TableCell>{item.strain_type || item.sensor || 'N/A'}</TableCell>
                            <TableCell align="right">
                              {item.strain_value != null ? Number(item.strain_value).toFixed(4) : 'N/A'}
                            </TableCell>
                            <TableCell align="right">{Number(item.score).toFixed(6)}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Showing {results.anomaly_scores.filter((item) => item.is_anomaly).length} anomaly points.
                </Typography>
              </Paper>
            )}

            {/* Detailed Scores Table (first 50) */}
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Detailed Anomaly Scores (showing first 50)
              </Typography>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date & Time</TableCell>
                      <TableCell>Sensor</TableCell>
                      <TableCell align="right">Score</TableCell>
                      <TableCell align="center">Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.anomaly_scores.slice(0, 50).map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{item.datetime}</TableCell>
                        <TableCell>{item.sensor || 'N/A'}</TableCell>
                        <TableCell align="right">{item.score.toFixed(6)}</TableCell>
                        <TableCell align="center">
                          {item.is_anomaly ? (
                            <Chip label="Anomaly" color="error" size="small" />
                          ) : (
                            <Chip label="Normal" color="success" size="small" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {results.anomaly_scores.length > 50 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Showing first 50 of {results.anomaly_scores.length} total scores
                </Typography>
              )}
            </Paper>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default MOM;
